// "5,000 credits / month" $129/mo recurring add-on (#342, reworked #357). PURCHASE/DISPLAY surface only: the add-on's
// `credits` entitlement adds 5,000 to the monthly allowance, a HARD allowance like any plan's (no unlimited bypass). Mirrors
// auto-replies-addon.ts and reuses the same multi-item checkout path (getCheckoutPageUrl +
// subscription_items + merge_items) — no forked flow.
//
// Chargebee item prices (addon item `Unlimited-Credits`; ids kept from the old Unlimited add-on, renamed in Chargebee):
//   - `Unlimited-Credits-USD-Monthly` = $129.00 / month
//   - `Unlimited-Credits-USD-Yearly`  = $1,290.00 / year (10 x monthly)
// A recurring add-on must match the subscription's billing period, so the price follows the plan's cycle.

export type UnlimitedCreditsCycle = 'monthly' | 'annual';

export const UNLIMITED_CREDITS_MONTHLY_ITEM_PRICE_ID = 'Unlimited-Credits-USD-Monthly';
export const UNLIMITED_CREDITS_YEARLY_ITEM_PRICE_ID = 'Unlimited-Credits-USD-Yearly';
export const UNLIMITED_CREDITS_MONTHLY_PRICE = 129;
export const UNLIMITED_CREDITS_YEARLY_PRICE = 1290;

/**
 * Billing cycle of a subscription from its Chargebee plan item-price id (`subscription.plan_id`).
 * EXACT map of the plan item prices that exist on Chargebee prod and test (checked read-only 2026-10-22:
 * ProTrial, StandardNew, Basic, Standard, Pro, each -USD-Monthly / -USD-Yearly; none archived).
 * Unknown ids return null — the add-on is then NOT offered, because a wrong-period add-on cannot attach.
 */
const PLAN_CYCLES: ReadonlyMap<string, UnlimitedCreditsCycle> = new Map(
  ['ProTrial', 'StandardNew', 'Basic', 'Standard', 'Pro'].flatMap((family): Array<[string, UnlimitedCreditsCycle]> => [
    [`${family}-USD-Monthly`, 'monthly'],
    [`${family}-USD-Yearly`, 'annual'],
  ]),
);

export function planCycleFromPlanId(planId?: string | null): UnlimitedCreditsCycle | null {
  return (planId && PLAN_CYCLES.get(planId)) || null;
}

export const unlimitedCreditsItemPriceId = (cycle: UnlimitedCreditsCycle): string =>
  cycle === 'annual' ? UNLIMITED_CREDITS_YEARLY_ITEM_PRICE_ID : UNLIMITED_CREDITS_MONTHLY_ITEM_PRICE_ID;

export const unlimitedCreditsPrice = (cycle: UnlimitedCreditsCycle): number =>
  cycle === 'annual' ? UNLIMITED_CREDITS_YEARLY_PRICE : UNLIMITED_CREDITS_MONTHLY_PRICE;

/** True when the subscription already carries either price of the add-on. */
export function isUnlimitedCreditsActive(ownedItemPriceIds?: string[] | null): boolean {
  if (!ownedItemPriceIds || ownedItemPriceIds.length === 0) return false;
  return ownedItemPriceIds.some(
    (id) => id === UNLIMITED_CREDITS_MONTHLY_ITEM_PRICE_ID || id === UNLIMITED_CREDITS_YEARLY_ITEM_PRICE_ID,
  );
}

export interface UnlimitedCreditsAddonState {
  show: boolean;
  isActive: boolean;
  canBuy: boolean;
}

/** Recurring add-on: only for active subscribers (it merges into the subscription); "Active" when owned. */
export function getUnlimitedCreditsAddonState(params: {
  hasActiveSubscription: boolean;
  ownedItemPriceIds?: string[] | null;
  /** Exact cycle from planCycleFromPlanId; null (unknown plan) hides the card. */
  cycle?: UnlimitedCreditsCycle | null;
}): UnlimitedCreditsAddonState {
  const isActive = isUnlimitedCreditsActive(params.ownedItemPriceIds);
  const show = Boolean(params.hasActiveSubscription) && (params.cycle !== undefined ? params.cycle !== null : true);
  return { show, isActive, canBuy: show && !isActive };
}

/** Checkout `subscription_items`, paired with `merge_items: true` (plan + other add-ons kept). */
export function buildUnlimitedCreditsCheckoutItems(
  cycle: UnlimitedCreditsCycle,
): Array<{ item_price_id: string; quantity: number }> {
  return [{ item_price_id: unlimitedCreditsItemPriceId(cycle), quantity: 1 }];
}
